import { API_CONFIG } from '../utils/apiConfig';
import { getAccessToken } from '../utils/apiClient';

const DEFAULT_BASE_URL = API_CONFIG.XAUTH_API_URL;

export interface XAuthServiceConfig {
    baseUrl?: string;
    /**
     * Optional access token override.
     * If not provided, the token will be automatically retrieved from session storage.
     */
    accessToken?: string;
}

export interface XAuthPermissionDetails {
    permissions: string[];
}

/**
 * Custom exception for XAuth API errors that may include scope information.
 */
export class XAuthApiException extends Error {
    public readonly errorCode?: number;
    public readonly missingScopes?: string[];

    constructor(message: string, errorCode?: number, missingScopes?: string[]) {
        super(message);
        this.name = 'XAuthApiException';
        this.errorCode = errorCode;
        this.missingScopes = missingScopes;
    }
}

/**
 * Fetches the list of target users (patients) for a therapist.
 *
 * @param therapistId The ID of the therapist
 * @param config Optional configuration including accessToken override
 * @returns Array of patient IDs (UIDs)
 */
export async function getTargetUsers(therapistId: string, config?: XAuthServiceConfig): Promise<string[]> {
    if (!therapistId) {
        throw new Error('Therapist ID is required');
    }

    const baseUrl = config?.baseUrl || DEFAULT_BASE_URL;
    if (!baseUrl) {
        throw new Error('Missing API base URL for XAuth service');
    }

    // For xAuth, we need to verify the token exists
    const accessToken = config?.accessToken ?? getAccessToken();
    if (!accessToken) {
        throw new Error('Access token is required for XAuth requests');
    }

    // According to OpenAPI spec: GET /{permittedUserId} returns list of target users
    const url = buildUrl(baseUrl, `/${therapistId}`);

    const headers: HeadersInit = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
    };

    let response: Response;
    try {
        response = await fetch(url, {
            method: 'GET',
            headers,
        });
    } catch (networkError) {
        console.error('[XAuthService] Network error while calling XAuth server', networkError);
        throw networkError;
    }

    if (!response.ok) {
        // 404 might mean user not found or no patients - treat as empty list for getTargetUsers
        if (response.status === 404) {
            return [];
        }

        let message: string | undefined;
        let errorCode: number | undefined;
        let missingScopes: string[] | undefined;

        try {
            const body = await response.text();
            // Try to parse as JSON to extract error details
            try {
                const errorData = JSON.parse(body);
                message = errorData.message || errorData.error || body;
                errorCode = errorData.errorCode;
                missingScopes = errorData.missingScopes;
            } catch {
                message = body;
            }
        } catch {
            message = response.statusText;
        }

        console.error('[XAuthService] XAuth server responded with error', {
            status: response.status,
            message,
            errorCode,
            missingScopes,
        });

        throw new XAuthApiException(
            message || `XAuth request failed with status ${response.status}`,
            errorCode,
            missingScopes,
        );
    }

    try {
        return (await response.json()) as string[];
    } catch {
        return [];
    }
}

/**
 * Returns the named cross-account permissions for one therapist/patient pair.
 */
export async function getPermissions(
    permittedUserId: string,
    targetUserId: string,
    config?: XAuthServiceConfig,
): Promise<string[]> {
    if (!permittedUserId || !targetUserId) {
        throw new Error('Permitted user ID and target user ID are required');
    }

    const baseUrl = config?.baseUrl || DEFAULT_BASE_URL;
    if (!baseUrl) {
        throw new Error('Missing API base URL for XAuth service');
    }
    const accessToken = config?.accessToken ?? getAccessToken();
    if (!accessToken) {
        throw new Error('Access token is required for XAuth requests');
    }

    const url = buildUrl(baseUrl, `/${encodeURIComponent(permittedUserId)}/${encodeURIComponent(targetUserId)}`);
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });

    if (response.status === 404) {
        return [];
    }
    if (!response.ok) {
        const body = await response.text();
        let message = body || response.statusText || `XAuth request failed with status ${response.status}`;
        let errorCode: number | undefined;
        let missingScopes: string[] | undefined;
        try {
            const parsed = JSON.parse(body) as {
                message?: string;
                error?: string;
                errorCode?: number;
                missingScopes?: string[];
            };
            message = parsed.message || parsed.error || message;
            errorCode = parsed.errorCode;
            missingScopes = parsed.missingScopes;
        } catch {
            // Plain-text error bodies are valid xAuth responses.
        }
        throw new XAuthApiException(message, errorCode, missingScopes);
    }

    const details = (await response.json()) as Partial<XAuthPermissionDetails>;
    return Array.isArray(details.permissions)
        ? details.permissions.filter((permission): permission is string => typeof permission === 'string')
        : [];
}

/**
 * Lists only target users whose relationship contains the requested permission.
 */
export async function getTargetUsersWithPermission(
    permittedUserId: string,
    permission: string,
    config?: XAuthServiceConfig,
): Promise<string[]> {
    const targetUserIds = await getTargetUsers(permittedUserId, config);
    const permissionChecks = await Promise.all(
        targetUserIds.map(async (targetUserId) => ({
            targetUserId,
            permissions: await getPermissions(permittedUserId, targetUserId, config),
        })),
    );
    return permissionChecks
        .filter((check) => check.permissions.includes(permission))
        .map((check) => check.targetUserId);
}

/**
 * Response type for the resolve endpoint.
 */
export interface ResolvedUser {
    userId: string;
    username: string;
    firstName: string | null;
    lastName: string | null;
    birthdate?: string | null;
}

/**
 * Resolves a user ID to user information (name, username, etc.).
 *
 * @param userId The user ID to resolve
 * @param config Optional configuration including accessToken override
 * @returns Resolved user information
 */
export async function resolveUserId(userId: string, config?: XAuthServiceConfig): Promise<ResolvedUser> {
    if (!userId) {
        throw new Error('User ID is required');
    }

    const baseUrl = config?.baseUrl || DEFAULT_BASE_URL;
    if (!baseUrl) {
        throw new Error('Missing API base URL for XAuth service');
    }

    const accessToken = config?.accessToken ?? getAccessToken();
    if (!accessToken) {
        throw new Error('Access token is required for XAuth requests');
    }

    // Endpoint: GET /resolve/{userId}
    const url = buildUrl(baseUrl, `/resolve/${userId}`);
    const headers: HeadersInit = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
    };

    let response: Response;
    try {
        response = await fetch(url, {
            method: 'GET',
            headers,
        });
    } catch (networkError) {
        throw networkError;
    }

    if (!response.ok) {
        let message: string | undefined;
        let errorCode: number | undefined;
        let missingScopes: string[] | undefined;

        try {
            const body = await response.text();
            try {
                const errorData = JSON.parse(body);
                message = errorData.message || errorData.error || body;
                errorCode = errorData.errorCode;
                missingScopes = errorData.missingScopes;
            } catch {
                message = body;
            }
        } catch {
            message = response.statusText;
        }

        console.error('[XAuthService] XAuth resolve endpoint responded with error', {
            status: response.status,
            message,
            errorCode,
            missingScopes,
        });

        throw new XAuthApiException(
            message || `XAuth resolve request failed with status ${response.status}`,
            errorCode,
            missingScopes,
        );
    }

    try {
        return (await response.json()) as ResolvedUser;
    } catch (parseError) {
        throw new Error('Failed to parse user resolution response');
    }
}

/**
 * Builds a full URL from base URL and path.
 */
function buildUrl(baseUrl: string, path: string): string {
    const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedBase}${normalizedPath}`;
}
